import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/pages/layoutChrome";
import { Button } from "@/components/ui/button";
import ModuleClientSelector from "@/components/ModuleClientSelector";
import SatFiscalDocsPanel from "@/components/SatFiscalDocsPanel";
import { usePersistedClientId } from "@/lib/clientSelection";
import { TramitesEmbeddedPanel } from "@/modules/tramites/embeddedPanels";
import {
  TRAMITES_CONTEXT_LABEL,
  TRAMITES_TAB_CONFIG,
  TRAMITES_TAB_IDS,
  normalizeTramitesTabId,
} from "@/modules/tramites/manifest";
import { FISCAL_DOCS_OWNER } from "@/modules/sat/fiscalDocsOwnership";
import { DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH } from "@/modules/datosMaestros/datosMaestrosHub";
import { TRAMITES_VALIDADOR_NSS_PATH } from "@/lib/checkIdLabels";
import {
  TRAMITES_INSCRIPCION_IMSS_PATH,
  TRAMITES_INSCRIPCION_SAT_PATH,
  TRAMITES_MATERIALIDAD_PATH,
  TRAMITES_VALIDACION_RFC_SAT_PATH,
} from "@/modules/tramites/tramitesService";
import {
  isTramitesPlaceholderDownloadAction,
  isTramitesPlaceholderDownloadTab,
} from "@/modules/tramites/tramitesDownloadPlaceholder";
import { BadgeCheck, Building2, Download, Landmark, UserPlus, Users, ArrowLeft } from "lucide-react";

const ICON_BY_KEY = {
  landmark: Landmark,
  validate: BadgeCheck,
  userplus: UserPlus,
  users: Users,
  building: Building2,
};

function renderPlaceholderActions(actions) {
  const groups = [];
  let currentGroup = null;
  for (const action of actions || []) {
    const group = action.group || "";
    if (group !== currentGroup) {
      groups.push({ type: "heading", label: group });
      currentGroup = group;
    }
    groups.push({ type: "action", action });
  }

  return groups.map((entry, idx) => {
    if (entry.type === "heading" && entry.label) {
      return (
        <p
          key={`group-${entry.label}-${idx}`}
          className="text-[10px] font-bold uppercase tracking-wider text-[#0F2D5C]/70 pt-2 first:pt-0"
        >
          {entry.label}
        </p>
      );
    }
    const action = entry.action;
    return (
      <div
        key={action.id}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border border-slate-200 px-4 py-3 bg-slate-50/60"
      >
        <span className="text-sm font-medium text-slate-800">{action.label}</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled
          className="rounded-sm shrink-0 cursor-not-allowed opacity-70"
          data-testid={`tramites-download-${action.id}`}
          aria-disabled="true"
        >
          Próximamente
        </Button>
      </div>
    );
  });
}

function TramiteTabPanel({ tab, clientId }) {
  const Icon = ICON_BY_KEY[tab.iconKey] || Landmark;

  if (tab.id === "sat") {
    return (
      <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/80 px-6 py-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-[#0F2D5C]/10 p-2">
              <Icon className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0F2D5C]">{tab.title}</h2>
              <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">{tab.description}</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <SatFiscalDocsPanel clientId={clientId} compact accessMode="delegated" />
          {(tab.actions || []).some((a) => isTramitesPlaceholderDownloadAction(a.id)) ? (
            <p
              className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
              data-testid="tramites-sat-placeholder-notice"
            >
              Algunas descargas del tab SAT están <strong>en desarrollo</strong> (p. ej. acuse de declaración).
              CSF y opinión se gestionan arriba con la e.Firma del cliente.
            </p>
          ) : null}
          {(tab.actions || [])
            .map((action) => (
              <div
                key={action.id}
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border border-slate-200 px-4 py-3 ${
                  isTramitesPlaceholderDownloadAction(action.id) ? "bg-slate-50/60" : "hover:border-[#0F2D5C]/25 transition-colors"
                }`}
              >
                <span className="text-sm font-medium text-slate-800">{action.label}</span>
                {isTramitesPlaceholderDownloadAction(action.id) ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled
                    className="rounded-sm shrink-0 cursor-not-allowed opacity-70"
                    data-testid={`tramites-download-${action.id}`}
                    aria-disabled="true"
                  >
                    Próximamente
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled
                    className="rounded-sm shrink-0 cursor-not-allowed opacity-70"
                    data-testid={`tramites-download-${action.id}`}
                    aria-disabled="true"
                    title="Función no conectada todavía"
                  >
                    Próximamente
                  </Button>
                )}
              </div>
            ))}
        </div>
      </div>
    );
  }

  if (isTramitesPlaceholderDownloadTab(tab.id)) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden"
        data-testid={`tramites-placeholder-panel-${tab.id}`}
      >
        <div className="border-b border-slate-100 bg-slate-50/80 px-6 py-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-[#0F2D5C]/10 p-2">
              <Icon className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0F2D5C]">{tab.title}</h2>
              <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">{tab.description}</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p
            className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            data-testid="tramites-placeholder-notice"
          >
            Las descargas de este trámite están <strong>en desarrollo</strong> y estarán disponibles en una fase
            posterior. No se ejecuta ninguna operación al pulsar las acciones siguientes.
          </p>
          {(tab.actions || []).length ? renderPlaceholderActions(tab.actions) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-slate-100 bg-slate-50/80 px-6 py-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-[#0F2D5C]/10 p-2">
            <Icon className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-[#0F2D5C]">{tab.title}</h2>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">{tab.description}</p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-3">
        {(tab.actions || []).map((action) => (
          <div
            key={action.id}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border border-slate-200 px-4 py-3 hover:border-[#0F2D5C]/25 transition-colors"
          >
            <span className="text-sm font-medium text-slate-800">{action.label}</span>
            <Button
              type="button"
              size="sm"
              className="rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90 shrink-0"
              onClick={() => {
                alert("Descargando...");
              }}
              data-testid={`tramites-download-${action.id}`}
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function resolveActiveTab(searchParams) {
  const tabParam = searchParams.get("tab");
  if (!tabParam) return null;
  const raw = normalizeTramitesTabId(tabParam);
  return TRAMITES_TAB_CONFIG.find((t) => t.id === raw) || null;
}

const DEDICATED_TAB_REDIRECTS = {
  [TRAMITES_TAB_IDS.VALIDACION_RFC]: DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
  [TRAMITES_TAB_IDS.INSCRIPCION_SAT]: TRAMITES_INSCRIPCION_SAT_PATH,
  [TRAMITES_TAB_IDS.INSCRIPCION_IMSS]: TRAMITES_INSCRIPCION_IMSS_PATH,
  [TRAMITES_TAB_IDS.VALIDADOR_NSS]: TRAMITES_VALIDADOR_NSS_PATH,
  [TRAMITES_TAB_IDS.MATERIALIDAD]: TRAMITES_MATERIALIDAD_PATH,
};

/**
 * Detalle de un trámite (/tramites-fiscales?tab=…). Sin barra de pestañas; el hub está en /tramites/inicio.
 */
export default function TramitesFiscalesPage() {
  const [searchParams] = useSearchParams();
  const activeTab = useMemo(() => resolveActiveTab(searchParams), [searchParams]);
  const [clients, setClients] = useState([]);
  const [clientId] = usePersistedClientId(clients, { autoPickFirst: false });

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(Array.isArray(data) ? data : [])).catch(() => setClients([]));
  }, []);

  if (!searchParams.get("tab")) {
    return <Navigate to="/tramites/inicio" replace />;
  }

  if (!activeTab || activeTab.id === TRAMITES_TAB_IDS.SAT) {
    return <Navigate to={FISCAL_DOCS_OWNER.route} replace />;
  }

  if (DEDICATED_TAB_REDIRECTS[activeTab.id]) {
    return <Navigate to={DEDICATED_TAB_REDIRECTS[activeTab.id]} replace />;
  }

  const isEmbedded = Boolean(activeTab.embedded);
  const contentMaxWidth = isEmbedded ? "max-w-7xl" : "max-w-5xl";

  return (
    <div className="min-h-full" data-testid="tramites-fiscales-page">
      <PageHeader
        title={activeTab.title}
        subtitle={activeTab.description}
        breadcrumb={[TRAMITES_CONTEXT_LABEL, activeTab.label]}
      />
      <div className="px-8 pt-2 max-w-6xl">
        <Link
          to="/tramites/inicio"
          className="inline-flex items-center gap-2 rounded-sm border border-[#0F2D5C]/25 bg-[#0F2D5C]/[0.04] px-3 py-1.5 text-xs font-semibold text-[#0F2D5C] shadow-sm hover:bg-[#0F2D5C]/10 hover:border-[#0F2D5C]/40 transition-colors mb-4"
          data-testid="back-to-tramites-hub"
        >
          <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={2.25} />
          Volver a {TRAMITES_CONTEXT_LABEL}
        </Link>
      </div>
      <div className="px-8 pt-0 max-w-6xl">
        <ModuleClientSelector variant="panel" className="mb-6" />
      </div>
      <div className={`p-8 pt-0 ${contentMaxWidth}`}>
        {isEmbedded ? (
          <TramitesEmbeddedPanel tabId={activeTab.embedded} />
        ) : (
          <TramiteTabPanel tab={activeTab} clientId={clientId} />
        )}
      </div>
    </div>
  );
}
